import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/** Products → Lots and COAs (backend lib/lots.ts). */
export type Lot = {
  id: string
  lot_number: string
  product_id: string | null
  variant_id: string | null
  sku: string | null
  title: string | null
  coa_url: string | null
  coa_name: string | null
  manufactured_on: string | null
  expires_on: string | null
  notes: string | null
  is_current: boolean
  created_at: string
  source?: "auto" | "staff"
}
export type LotInput = Partial<Pick<Lot, "lot_number" | "variant_id" | "coa_url" | "coa_name" | "manufactured_on" | "expires_on" | "notes" | "is_current">>
export type OrderLotLine = { line_item_id: string; variant_id: string | null; title: string; sku: string | null; quantity: number; lots: Lot[]; choices: Lot[] }

export function useLots(q: string) {
  return useQuery({ queryKey: ["lots", q], queryFn: () => sdk.client.fetch<{ lots: Lot[] }>("/admin/terify/lots", { query: { q: q.trim() || undefined } }) })
}

export function useSaveLot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: LotInput & { id?: string }) => sdk.client.fetch<{ lot: Lot }>(id ? `/admin/terify/lots/${id}` : "/admin/terify/lots", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lots"] }),
  })
}

export function useDeleteLot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => sdk.client.fetch(`/admin/terify/lots/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lots"] }),
  })
}

export function useOrderLots(orderId: string) {
  return useQuery({ queryKey: ["order-lots", orderId], queryFn: () => sdk.client.fetch<{ lines: OrderLotLine[] }>(`/admin/terify/orders/${orderId}/lots`) })
}

export function useSetOrderLots(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (lines: { line_item_id: string; lot_ids: string[] }[]) => sdk.client.fetch<{ lines: OrderLotLine[] }>(`/admin/terify/orders/${orderId}/lots`, { method: "POST", body: { lines } }),
    onSuccess: (data) => qc.setQueryData(["order-lots", orderId], data),
  })
}

/** Product options for the lot form: products matching `q` with their variants. */
export function useVariantSearch(q: string) {
  return useQuery({
    queryKey: ["lots", "variants", q],
    queryFn: () => sdk.client.fetch<{ products: { id: string; title: string; variants: { id: string; title: string; sku: string | null }[] }[] }>("/admin/products", { query: { q: q.trim() || undefined, limit: 20, fields: "id,title,variants.id,variants.title,variants.sku" } }),
  })
}
